import React from 'react';
import isMobile from '$/utils/isMobile';
import { useHasChangedShallow } from '$/hooks/useHasChanged';

export interface WebinarPlayerRenderContextState {
  iDock?: boolean;
  joinRoom?: boolean;
  testControlBar?: boolean;
  messagesThread?: boolean;
  gdprBannerWidget?: boolean;
  interactionStream?: boolean;
  nonCardInteractions?: boolean;
  snapToCardOnPauseOrPlayTrigger?: boolean;
  snapToCardOnPauseOrPlay?: boolean;
}

export interface WaitForProps {
  waitFor?: keyof WebinarPlayerRenderContextState;
  delay?: number;
  timeout: number;
  notifyOnUnmount?: boolean;
}

export const waitForConfig: Record<keyof WebinarPlayerRenderContextState, WaitForProps> = {
  iDock: {
    waitFor: 'interactionStream',
    timeout: 3000,
  },
  joinRoom: {
    timeout: 0,
  },
  testControlBar: {
    waitFor: 'messagesThread',
    timeout: 4000,
  },
  messagesThread: {
    waitFor: 'joinRoom',
    delay: isMobile ? 1000 : 0,
    timeout: isMobile ? 2000 : 1000,
  },
  gdprBannerWidget: {
    waitFor: 'messagesThread',
    timeout: 4000,
  },
  interactionStream: {
    waitFor: 'messagesThread',
    timeout: isMobile ? 2000 : 500,
    delay: 500,
  },
  nonCardInteractions: {
    waitFor: 'iDock',
    timeout: 4000,
  },
  snapToCardOnPauseOrPlayTrigger: {
    timeout: 200,
    notifyOnUnmount: true,
  },
  snapToCardOnPauseOrPlay: {
    waitFor: 'snapToCardOnPauseOrPlayTrigger',
    timeout: 0,
  },
};

export interface WebinarPlayerRenderContextValue extends WebinarPlayerRenderContextState {
  onMounted: (key: keyof WebinarPlayerRenderContextState) => void;
  onUnmounted: (key: keyof WebinarPlayerRenderContextState) => void;
}

export const WebinarPlayerRenderContext = React.createContext<WebinarPlayerRenderContextValue>({
  onMounted: () => {},
  onUnmounted: () => {},
});

export const WebinarPlayerRenderProvider: React.FC<{ children: React.ReactNode }> = React.memo(
  ({ children }) => {
    const [state, setState] = React.useState<WebinarPlayerRenderContextState>({});
    const onMounted = React.useCallback((key: keyof WebinarPlayerRenderContextState) => {
      setState((current) => ({
        ...current,
        [key]: true,
      }));
    }, []);

    const onUnmounted = React.useCallback((key: keyof WebinarPlayerRenderContextState) => {
      setState((current) => ({
        ...current,
        [key]: false,
      }));
    }, []);

    const value = React.useMemo(
      () => ({
        ...state,
        onMounted,
        onUnmounted,
      }),
      [onMounted, onUnmounted, state]
    );

    return (
      <WebinarPlayerRenderContext.Provider value={value}>
        {children}
      </WebinarPlayerRenderContext.Provider>
    );
  }
);

export const useWebinarPlayerRenderContext = (): WebinarPlayerRenderContextValue =>
  React.useContext(WebinarPlayerRenderContext);

export const WaitFor: React.FC<{
  element: keyof WebinarPlayerRenderContextState;
  render?: () => React.ReactNode;
  children?: React.ReactNode;
}> = React.memo(({ element, children, render }) => {
  const { timeout, delay, notifyOnUnmount, waitFor } = waitForConfig[element];
  const setShouldShowTimer = React.useRef<ReturnType<typeof setTimeout>>();
  const [shouldShow, setShouldShow] = React.useState(!waitFor && !timeout);
  const { onUnmounted, onMounted: onMounted, ...state } = useWebinarPlayerRenderContext();
  const waitForState = waitFor && state[waitFor];

  const hasWaitForStateChanged = useHasChangedShallow([waitForState]);
  React.useEffect(() => {
    if (waitFor && hasWaitForStateChanged && !waitForState && shouldShow) {
      setShouldShow(false);
    }
  }, [shouldShow, waitFor, waitForState, hasWaitForStateChanged]);

  React.useEffect(() => {
    if (waitForState && !shouldShow) {
      if (setShouldShowTimer.current) return;
      if (!delay) {
        return setShouldShow(true);
      }
      setShouldShowTimer.current = setTimeout(() => setShouldShow(true), delay);
    }
  }, [delay, shouldShow, waitForState]);

  React.useEffect(() => {
    if (shouldShow) return;
    const timer =
      timeout &&
      setTimeout(() => {
        setShouldShow(true);
      }, timeout);
    return (): void => {
      timer && clearTimeout(timer);
    };
  }, [timeout, shouldShow]);

  const shouldShowChanged = useHasChangedShallow([shouldShow]);
  React.useEffect(() => {
    if (shouldShow && shouldShowChanged) {
      onMounted(element);
    }
  }, [shouldShow, element, onMounted, shouldShowChanged]);

  React.useEffect(() => {
    return (): void => {
      if (!notifyOnUnmount) return;
      onUnmounted(element);
    };
  }, [element, notifyOnUnmount, onUnmounted]);

  if (!shouldShow) return null;
  return <>{render?.() ?? children}</>;
});
